import { gql } from '@apollo/client'

export const configurationTabQuery = gql`
    query configurationTabQuery($id: UUID!) {
        quote(id: $id) {
            hasITARRestrictions
            id
            type
            status
            name
            user {
                id
                email
            }
            project {
                id
                isSecurePlus
                name
            }
            team {
                id
                name
            }
            quoteModels(limit: 1000) {
                totalCount
                items {
                    id
                    quoteModelRevisions {
                        id
                        dfmReports {
                            isPublished
                        }
                    }
                    selectedQuoteModelRevision {
                        isITARProhibitedViewing
                        id
                        quantityRangeStatus
                        version
                        state
                        partRevision {
                            id
                            modelFileName
                            downloadUri
                        }
                        processesDetails {
                            isBusinessAccountDiscountApplied
                            postProcesses {
                                postProcess {
                                    id
                                    name
                                    typeName
                                }
                                postProcessThickness {
                                    id
                                    name
                                }
                                postProcessColor {
                                    id
                                    name
                                }
                            }
                            totalPrice {
                                ... on ExactPrice {
                                    priceInCents
                                    unitPriceInCents
                                }
                                ... on EstimatedPrice {
                                    lowPriceInCents
                                    lowUnitPriceInCents
                                    highPriceInCents
                                    highUnitPriceInCents
                                }
                            }
                            secondaryProcesses {
                                process {
                                    name
                                }
                            }
                        }
                        modelConfiguration {
                            id
                            process {
                                id
                                name
                                platformProcessTypeId
                            }
                            materialGrade
                            color {
                                id
                                name
                            }
                            colorCode
                            shore {
                                id
                                name
                            }
                            texture {
                                id
                                name
                            }
                            material {
                                ... on Material {
                                    id
                                    name
                                }
                            }
                            productionLeadTime {
                                priorityId
                                productionDays
                                shipDateEstimate {
                                    shippedByDate
                                }
                            }
                            quantity
                            textureCode
                        }
                        attachmentLink {
                            id
                            createdAt
                            quoteAttachmentRevision {
                                attachment {
                                    ... on Attachment {
                                        id
                                        fileName
                                        downloadUrl
                                    }
                                }
                            }
                        }
                        dfmFeedback {
                            id
                            accepted
                            severity
                            message
                            isPublished
                        }
                        dfmReports {
                            quoteAttachmentRevisionId
                            asset {
                                id
                                createdAt
                                downloadUrl
                                fileName
                            }
                            isPublished
                        }
                        taps {
                            id
                        }
                        holes {
                            id
                            tapOptions {
                                id
                            }
                        }
                        drawingRequirements
                    }
                }
            }
            quoteAttachments(limit: 1000) {
                totalCount
                items {
                    id
                    latestAttachmentRevision {
                        id
                        attachment {
                            ... on Attachment {
                                id
                                fileName
                                downloadUrl
                                annotated {
                                    id
                                    fileName
                                    downloadUrl
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export const coreQuoteDetailsShipping = gql`
    query coreQuoteDetailsShipping($id: UUID!, $address: AddressInput) {
        quote(id: $id) {
            id
            name
            isOverseasShipping
            zipCode
            hasITARRestrictions
            isEligibleForFreeShipping
            quoteItems(limit: 1000) {
                totalCount
                items {
                    id
                    quoteModelRevision {
                        isITARProhibitedViewing
                        id
                        fileName
                        mold {
                            id
                            leadTimeDays
                            estimatedSampleDeliveryDate(address: $address)
                        }
                    }
                }
            }
        }
    }
`

export const coreQuoteDetailsQuery = gql`
    query coreQuoteDetails(
        $id: UUID!
        $isImMaafcoEnabled: Boolean = false
        $isCpqFlagV2Enabled: Boolean = false
    ) {
        quote(id: $id) {
            id
            name
            status
            zipCode
            isTaxExempt
            numberOfParts
            partsSummary
            needByDate
            lastModified
            isLocked
            isCpqOwned
            isQuoteInRfqState
            isQuoteIMReorder
            type
            beganCheckoutAt
            productionPartsSubtotalInCents
            moldToolSubtotalInCents
            depositInCents
            subTotalInCents
            slowestShipDateEstimate
            isOverseasShipping
            expiration
            isExpired
            isQuotePurchasable
            earliestValidUntilDate
            reorderSourceOrderName
            isFlexiblePriceQuote
            notesToCustomer
            deletedAt
            isMoldRepair
            isLocked
            isCpqQuote @include(if: $isCpqFlagV2Enabled)
            isCpqOwned
            hasITARRestrictions
            isEligibleForFreeShipping
            cpqQuote @include(if: $isCpqFlagV2Enabled) {
                sfdcId
                platformQuote {
                    id
                }
                quotePdfUrl
                orderTotal
                partsCount
                moldCount
                orderQuantity
            }
            project {
                id
                isSecurePlus
                name
            }
            team {
                id
                name
            }
            isQuoteIMReorder
            platformOrder {
                name
                purchaserEmail
                purchasedDate
                purchaserUserId
            }
            purchaseInfo {
                purchaserEmail
                purchasedDate
                purchaserUserId
            }
            orderName
            businessAccountDiscount {
                id
                isActive
                endAt
                percent
            }
            user {
                id
            }
            quoteAttachments(limit: 1000) {
                totalCount
                items {
                    id
                    latestAttachmentRevision {
                        id
                        attachment {
                            ... on Attachment {
                                id
                                fileName
                                downloadUrl
                                annotated {
                                    id
                                    fileName
                                    downloadUrl
                                }
                                sanitized {
                                    id
                                    fileName
                                    downloadUrl
                                }
                            }
                        }
                    }
                }
            }
            quoteModels(limit: 1000) {
                totalCount
                items {
                    id
                    quoteModelRevisions {
                        id
                        attachmentLink {
                            id
                        }
                    }
                    selectedQuoteModelRevision {
                        isITARProhibitedViewing
                        id
                        fileName
                        createdAt
                        quantityRangeStatus
                        state
                        version
                        taps {
                            id
                        }
                        holes {
                            id
                            groupKey
                            patches {
                                id
                            }

                            tapOptions {
                                id
                            }
                            tapAnnotation {
                                id
                                depth
                                tapOption {
                                    id
                                }
                            }
                        }

                        mold {
                            id
                            name @include(if: $isImMaafcoEnabled)
                            leadTimeDays
                            customerToolName @include(if: $isImMaafcoEnabled)
                            material {
                                id
                                name
                            }
                            price {
                                priceInCents
                            }
                            toolLife
                            uiIndex
                            moldToolRevisionQmr
                                @include(if: $isImMaafcoEnabled) {
                                moldToolRevisionId
                                quoteModelRevisionId
                                cavityPerPart
                            }
                        }
                        partRevision {
                            id
                            analysisErrorCode
                            updatedAt
                            modelFileName
                            downloadUri
                            thumbnailUri
                            status
                            sceneBundle
                            boundingBox {
                                x
                                y
                                z
                            }
                        }
                        dfmFeedback {
                            id
                            accepted
                            severity
                            message
                            isPublished
                        }
                        dfmReports {
                            quoteAttachmentRevisionId
                            asset {
                                id
                                createdAt
                                downloadUrl
                                fileName
                            }
                            isPublished
                        }
                        modelConfiguration {
                            id
                            dfmInspection {
                                id
                                inspectionData
                                isPublished
                            }
                            productionLeadTime {
                                id
                                priorityId
                                productionDays
                            }
                            material {
                                ... on Material {
                                    id
                                    name
                                }
                                ... on UserEnteredMaterial {
                                    id
                                    name
                                    process {
                                        id
                                        name
                                    }
                                }
                            }
                            color {
                                id
                                name
                            }
                            colorCode
                            shore {
                                id
                                name
                            }
                            texture {
                                id
                                name
                            }
                            textureCode
                            process {
                                id
                                name
                                platformProcessTypeId
                            }
                            quantity
                            requiresOvermold
                            materialGrade
                            fdmInfillPercentageCategory
                            fdmInfillPercentageCategoryLabel
                        }
                        quoteModel {
                            id
                            quote {
                                id
                                type
                            }
                        }
                        annotations {
                            id
                        }
                        processesDetails {
                            isBusinessAccountDiscountApplied
                            primaryProcess {
                                process {
                                    id
                                    name
                                    platformProcessTypeId
                                }
                            }
                            postProcesses {
                                postProcess {
                                    id
                                    name
                                    typeName
                                }
                                unitPriceInCents
                                postProcessLeadTimeInDays
                                postProcessThickness {
                                    id
                                    name
                                }
                                postProcessColor {
                                    id
                                    name
                                }
                                postProcessNumParts
                                postProcessInspectionCriteria
                            }
                            totalPrice {
                                ... on ExactPrice {
                                    priceInCents
                                    unitPriceInCents
                                }
                                ... on EstimatedPrice {
                                    lowPriceInCents
                                    lowUnitPriceInCents
                                    highPriceInCents
                                    highUnitPriceInCents
                                }
                            }
                            secondaryProcesses {
                                process {
                                    id
                                    name
                                    typeName
                                }
                            }
                        }
                        supportedPriorityRegions {
                            partSupportsDomestic
                            partSupportsInternational
                        }
                        attachmentLink {
                            id
                            createdAt
                            isPdfOcrAutoQuotable
                            quoteAttachmentRevision {
                                id
                                attachment {
                                    ... on Attachment {
                                        id
                                        fileName
                                        downloadUrl
                                        annotated {
                                            id
                                            fileName
                                            downloadUrl
                                        }
                                        sanitized {
                                            id
                                            fileName
                                            downloadUrl
                                        }
                                    }
                                }
                            }
                        }
                        drawingRequirements
                    }
                }
            }
            dfmAssets(limit: 1000) {
                totalCount
                items {
                    id
                    fileName
                    downloadUrl
                }
            }
        }
    }
`

export const drawingAnalysisTabQuery = gql`
    query drawingAnalysisTabQuery($id: UUID!) {
        quote(id: $id) {
            hasITARRestrictions
            id
            name
            status
            type
            user {
                id
            }
            quoteModels(limit: 1000) {
                items {
                    id
                    quoteModelRevisions {
                        id
                        attachmentLink {
                            id
                        }
                    }
                    selectedQuoteModelRevision {
                        isITARProhibitedViewing
                        id
                        fileName
                        state
                        version
                        taps {
                            id
                        }
                        partRevision {
                            id
                            analysisErrorCode
                            updatedAt
                            modelFileName
                            downloadUri
                            thumbnailUri
                            status
                            sceneBundle
                            boundingBox {
                                x
                                y
                                z
                            }
                        }
                        dfmFeedback {
                            id
                            severity
                        }

                        modelConfiguration {
                            id
                            dfmInspection {
                                id
                                inspectionData
                            }
                            productionLeadTime {
                                id
                                priorityId
                                productionDays
                            }
                            material {
                                ... on Material {
                                    id
                                    name
                                }
                            }
                            color {
                                id
                                name
                            }
                            colorCode
                            textureCode

                            shore {
                                id
                                name
                            }
                            texture {
                                id
                                name
                            }
                            process {
                                id
                                name
                            }
                            quantity
                            requiresOvermold
                            materialGrade
                            additionalRequirements
                            hasMultipleFinishes
                            userNeedsConfigHelp
                        }

                        annotations {
                            id
                        }
                        processesDetails {
                            primaryProcess {
                                process {
                                    id
                                    name
                                    platformProcessTypeId
                                }
                            }
                            postProcesses {
                                postProcess {
                                    id
                                    name
                                    typeName
                                }
                                unitPriceInCents
                                postProcessLeadTimeInDays
                                postProcessThickness {
                                    id
                                    name
                                }
                                postProcessColor {
                                    id
                                    name
                                }
                                postProcessNumParts
                                postProcessInspectionCriteria
                            }
                            totalPrice {
                                ... on ExactPrice {
                                    priceInCents
                                    unitPriceInCents
                                }
                                ... on EstimatedPrice {
                                    lowPriceInCents
                                    lowUnitPriceInCents
                                    highPriceInCents
                                    highUnitPriceInCents
                                }
                            }
                            secondaryProcesses {
                                process {
                                    id
                                    name
                                    typeName
                                }
                            }
                        }
                        attachmentLink {
                            id
                            createdAt
                            isPdfOcrAutoQuotable
                            quoteAttachmentRevision {
                                id
                                attachment {
                                    ... on Attachment {
                                        id
                                        fileName
                                        downloadUrl
                                        annotated {
                                            id
                                            fileName
                                            downloadUrl
                                        }
                                    }
                                }
                            }
                        }
                        drawingRequirements
                    }
                }
            }
        }
    }
`

export const queryForFeedbackThreadsAddReqModalConfigTab = gql`
    query detailsForFeedbackThreadsAddReqModalConfigTab($id: UUID!) {
        quote(id: $id) {
            hasITARRestrictions
            id
            type
            status
            user {
                id
            }
            project {
                isSecurePlus
            }
            quoteModels(limit: 1000) {
                items {
                    id
                    quoteModelRevisions {
                        id
                    }
                    selectedQuoteModelRevision {
                        isITARProhibitedViewing
                        id
                        state
                        dfmFeedback {
                            id
                            accepted
                            severity
                        }
                        partRevision {
                            modelFileName
                        }

                        processesDetails {
                            postProcesses {
                                postProcess {
                                    id
                                    name
                                }
                            }
                            secondaryProcesses {
                                process {
                                    typeName
                                }
                            }
                            totalPrice {
                                ... on ExactPrice {
                                    priceInCents
                                    unitPriceInCents
                                }
                                ... on EstimatedPrice {
                                    lowPriceInCents
                                    lowUnitPriceInCents
                                    highPriceInCents
                                    highUnitPriceInCents
                                }
                            }
                        }
                        attachmentLink {
                            id
                            quoteAttachmentRevision {
                                id
                                attachment {
                                    ... on Attachment {
                                        id
                                    }
                                }
                            }
                        }
                        modelConfiguration {
                            id
                            dfmInspection {
                                id
                                inspectionData
                            }
                            process {
                                id
                            }
                            material {
                                ... on Material {
                                    id
                                }
                            }
                            productionLeadTime {
                                id
                                productionDays
                                shipDateEstimate {
                                    shippedByDate
                                }
                            }
                            additionalRequirements
                        }
                    }
                }
            }
        }
    }
`
export const queryForIMFeedbackAddReqUnconfiguredConfigModalTabs = gql`
    query detailsForIMFeedbackAddReqUnconfiguredConfigModalTabs($id: UUID!) {
        quote(id: $id) {
            hasITARRestrictions
            id
            type
            status
            user {
                id
            }
            quoteModels(limit: 1000) {
                items {
                    selectedQuoteModelRevision {
                        isITARProhibitedViewing
                        id
                        state
                        version
                        dfmReports {
                            isPublished
                        }
                        partRevision {
                            id
                            units
                        }
                        modelConfiguration {
                            process {
                                id
                            }
                            quantity
                            colorCode
                            materialGrade
                            quantity
                            textureCode
                            texture {
                                id
                            }
                            material {
                                ... on Material {
                                    id
                                    name
                                }
                                ... on UserEnteredMaterial {
                                    id
                                    name
                                    process {
                                        id
                                        name
                                    }
                                }
                            }
                            color {
                                id
                            }
                            productionLeadTime {
                                productionDays
                            }
                            dfmInspection {
                                inspectionData
                            }
                            additionalRequirements
                        }
                        dfmFeedback {
                            id
                            severity
                        }
                        processesDetails {
                            postProcesses {
                                postProcess {
                                    id
                                    typeName
                                }
                            }
                            secondaryProcesses {
                                process {
                                    id
                                }
                            }
                        }
                        attachmentLink {
                            quoteAttachmentRevision {
                                id
                            }
                        }
                        quoteModel {
                            id
                        }
                    }
                }
            }
        }
    }
`
